/**
 * Prescribe Action Modal
 *
 * Modal for creating prescriptions based on AI-extracted medication recommendations.
 * Pre-fills medication information from the parsed AI recommendation.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pill,
  Plus,
  Trash2,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
} from 'lucide-react';
import { ClinicalAction, MedicationInfo } from '@/lib/intellihealthActions';
import { FHIRPatient } from '@/services/fhirPatientsService';
import { prescriptionService } from '@/services/prescriptionService';
import { toast } from '@/hooks/use-toast';

interface PrescribeActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ClinicalAction | null;
  patient: FHIRPatient | null;
  onSubmit?: (prescriptionData: PrescriptionFormData) => Promise<void>;
}

interface MedicationFormItem extends MedicationInfo {
  id: string;
  selected: boolean;
  quantity?: number;
  refills?: number;
  sig?: string;
}

export interface PrescriptionFormData {
  patientId: string;
  medications: MedicationFormItem[];
  notes: string;
  sendToPharmacy: boolean;
}

// Common medication frequencies
const FREQUENCIES = [
  { value: 'QD', label: 'Once daily' },
  { value: 'BID', label: 'Twice daily' },
  { value: 'TID', label: 'Three times daily' },
  { value: 'QID', label: 'Four times daily' },
  { value: 'QHS', label: 'At bedtime' },
  { value: 'PRN', label: 'As needed' },
  { value: 'QOD', label: 'Every other day' },
  { value: 'Weekly', label: 'Once weekly' },
];

// Common routes
const ROUTES = [
  { value: 'PO', label: 'By mouth' },
  { value: 'Topical', label: 'Topical' },
  { value: 'Sublingual', label: 'Sublingual' },
  { value: 'Transdermal', label: 'Transdermal' },
  { value: 'IM', label: 'Intramuscular' },
  { value: 'SC', label: 'Subcutaneous' },
  { value: 'IV', label: 'Intravenous' },
  { value: 'Vaginal', label: 'Vaginal' },
];

const PrescribeActionModal: React.FC<PrescribeActionModalProps> = ({
  open,
  onOpenChange,
  action,
  patient,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendToPharmacy, setSendToPharmacy] = useState(false);

  // Initialize medications from action
  const [medications, setMedications] = useState<MedicationFormItem[]>(() => {
    if (!action?.metadata.medications) return [];

    return action.metadata.medications.map((med, index) => ({
      ...med,
      id: `med-${index}`,
      selected: true,
      quantity: med.quantity ?? 30,
      refills: med.refills ?? 0,
      sig: med.sig || (med.frequency ? `Take ${med.dose || ''} ${med.frequency}`.trim() : ''),
      frequency: med.frequency || 'QD',
      route: med.route || 'PO',
    }));
  });

  // Reset form when action changes
  React.useEffect(() => {
    if (action?.metadata.medications) {
      setMedications(
        action.metadata.medications.map((med, index) => ({
          ...med,
          id: `med-${index}`,
          selected: true,
          quantity: med.quantity ?? 30,
          refills: med.refills ?? 0,
          sig: med.sig || (med.frequency ? `Take ${med.dose || ''} ${med.frequency}`.trim() : ''),
          frequency: med.frequency || 'QD',
          route: med.route || 'PO',
        }))
      );
      setNotes('');
      setSendToPharmacy(false);
    }
  }, [action]);

  const selectedMedications = useMemo(() =>
    medications.filter(m => m.selected),
    [medications]
  );

  const handleMedicationToggle = (id: string) => {
    setMedications(prev =>
      prev.map(m => (m.id === id ? { ...m, selected: !m.selected } : m))
    );
  };

  const handleMedicationUpdate = (id: string, field: keyof MedicationFormItem, value: string | number | boolean) => {
    setMedications(prev =>
      prev.map(m => (m.id === id ? { ...m, [field]: value } : m))
    );
  };

  const handleAddMedication = () => {
    const newMed: MedicationFormItem = {
      id: `med-${Date.now()}`,
      name: '',
      dose: '',
      frequency: 'QD',
      route: 'PO',
      selected: true,
      quantity: 30,
      refills: 0,
      sig: '',
    };
    setMedications(prev => [...prev, newMed]);
  };

  const handleRemoveMedication = (id: string) => {
    setMedications(prev => prev.filter(m => m.id !== id));
  };

  const handleSubmit = async () => {
    if (!patient) {
      toast({
        title: 'Error',
        description: 'No patient selected',
        variant: 'destructive',
      });
      return;
    }

    if (selectedMedications.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one medication',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const prescriptionData: PrescriptionFormData = {
        patientId: patient.id,
        medications: selectedMedications,
        notes,
        sendToPharmacy,
      };

      if (onSubmit) {
        await onSubmit(prescriptionData);
      }

      toast({
        title: 'Success',
        description: `Prescription created for ${selectedMedications.length} medication(s)`,
      });

      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create prescription'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!action) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0" style={{ borderRadius: '0.75rem' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Pill className="h-4 w-4 text-emerald-600" />
                </div>
                Create Prescription
              </DialogTitle>
              <DialogDescription className="text-[hsl(var(--ih-text-muted))] mt-1">
                {patient && (
                  <span>
                    For <span className="text-slate-900 font-medium">{patient.first_name} {patient.last_name}</span>
                  </span>
                )}
              </DialogDescription>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border border-border text-muted-foreground transition-colors hover:bg-muted/70 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {/* AI Source Indicator */}
          <div className="flex items-center gap-2 px-3 py-2 bg-brand-teal/10 border border-brand-teal/20 rounded-lg">
            <Sparkles className="h-4 w-4 text-brand-teal" />
            <span className="text-xs text-brand-teal/70">
              Medications pre-filled from IntelliHealth AI recommendation
            </span>
          </div>

          {/* Medications List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Medications</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMedication}
                className="text-xs border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Medication
              </Button>
            </div>

            {medications.length === 0 ? (
              <div className="text-center py-8 text-[hsl(var(--ih-text-muted))]">
                <Pill className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No medications found in recommendation</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMedication}
                  className="mt-2 text-xs"
                >
                  Add Medication Manually
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {medications.map((med) => (
                  <div
                    key={med.id}
                    className={`p-4 rounded-lg border transition-all ${
                      med.selected
                        ? 'bg-slate-50 border-emerald-500/30'
                        : 'bg-slate-50/50 border-slate-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={med.selected}
                        onCheckedChange={() => handleMedicationToggle(med.id)}
                        className="mt-1"
                      />

                      <div className="flex-1 space-y-3">
                        {/* Medication Name and Dose */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Medication Name</Label>
                            <Input
                              value={med.name}
                              onChange={(e) => handleMedicationUpdate(med.id, 'name', e.target.value)}
                              placeholder="Medication name"
                              className="text-sm"
                              disabled={!med.selected}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Dose</Label>
                            <Input
                              value={med.dose || ''}
                              onChange={(e) => handleMedicationUpdate(med.id, 'dose', e.target.value)}
                              placeholder="e.g., 100mg"
                              className="text-sm"
                              disabled={!med.selected}
                            />
                          </div>
                        </div>

                        {/* Frequency and Route */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Frequency</Label>
                            <Select
                              value={med.frequency || 'QD'}
                              onValueChange={(value) => handleMedicationUpdate(med.id, 'frequency', value)}
                              disabled={!med.selected}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FREQUENCIES.map((freq) => (
                                  <SelectItem key={freq.value} value={freq.value}>
                                    {freq.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Route</Label>
                            <Select
                              value={med.route || 'PO'}
                              onValueChange={(value) => handleMedicationUpdate(med.id, 'route', value)}
                              disabled={!med.selected}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROUTES.map((route) => (
                                  <SelectItem key={route.value} value={route.value}>
                                    {route.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Quantity and Refills */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">Quantity</Label>
                            <Input
                              type="number"
                              value={med.quantity || 30}
                              onChange={(e) => handleMedicationUpdate(med.id, 'quantity', parseInt(e.target.value))}
                              className="text-sm"
                              disabled={!med.selected}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Refills</Label>
                            <Input
                              type="number"
                              value={med.refills || 0}
                              onChange={(e) => handleMedicationUpdate(med.id, 'refills', parseInt(e.target.value))}
                              min={0}
                              max={12}
                              className="text-sm"
                              disabled={!med.selected}
                            />
                          </div>
                        </div>

                        {/* Sig (Instructions) */}
                        <div>
                          <Label className="text-xs text-slate-600">Sig (Instructions)</Label>
                          <Input
                            value={med.sig || ''}
                            onChange={(e) => handleMedicationUpdate(med.id, 'sig', e.target.value)}
                            placeholder="e.g., Take 1 tablet by mouth daily"
                            className="text-sm"
                            disabled={!med.selected}
                          />
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMedication(med.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-slate-700">Additional Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional instructions or notes..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Send to Pharmacy */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="sendToPharmacy"
                checked={sendToPharmacy}
                onCheckedChange={(checked) => setSendToPharmacy(checked as boolean)}
              />
              <Label htmlFor="sendToPharmacy" className="text-slate-600 text-sm">
                Send to pharmacy after creation
              </Label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedMedications.length === 0}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Create Prescription ({selectedMedications.length})
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrescribeActionModal;
